import React, { useState } from 'react';

import { 

  Container, Form, Button, Image, Tabs, Tab, 

  Spinner, Alert, Badge, ListGroup, Card, Row, Col 

} from 'react-bootstrap';

import { analyzeImage } from '../services/api';


const ImageAnalysis = () => {

  const [image, setImage] = useState(null);

  const [imagePreview, setImagePreview] = useState(null);

  const [activeTab, setActiveTab] = useState('labels');

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);

  const [results, setResults] = useState({

    labels: null,

    faces: null,

    text: null,

    moderation: null

  });


  // Handle file selection

  const handleImageChange = (e) => {

    if (e.target.files[0]) {

      const selectedImage = e.target.files[0];

      setImage(selectedImage);

      setImagePreview(URL.createObjectURL(selectedImage));

      

      // Reset results when a new image is selected

      setResults({

        labels: null,

        faces: null,

        text: null,

        moderation: null

      });

    }

  };


  // Analyze image based on the active tab

  const analyzeSelectedImage = async () => {

    if (!image) return;

    

    setLoading(true);

    setError(null);


    try {

      let response;

      

      switch(activeTab) {

        case 'labels':

          response = await analyzeImage.detectLabels(image);

          setResults({ ...results, labels: response.data });

          break;

        

        case 'faces':

          response = await analyzeImage.detectFaces(image);

          setResults({ ...results, faces: response.data });

          break;

        

        case 'text':

          response = await analyzeImage.detectText(image);

          setResults({ ...results, text: response.data });

          break;

        

        case 'moderation':

          response = await analyzeImage.detectModeration(image);

          setResults({ ...results, moderation: response.data });

          break;

        

        default:

          break;

      }

    } catch (err) {

      setError(`Error analyzing image: ${err.response?.data?.error || err.message}`);

    } finally {

      setLoading(false);

    }

  };


  // Get confidence color class based on confidence score

  const getConfidenceColorClass = (confidence) => {

    if (confidence >= 80) return 'confidence-high';

    if (confidence >= 60) return 'confidence-medium';

    return 'confidence-low';

  };


  // Render labels results

  const renderLabelsResults = () => {

    if (!results.labels) return null;

    

    return (

      <div className="result-container">

        <h4>Detected Labels</h4>

        <p>Found {results.labels.Labels.length} labels in the image</p>

        

        <ListGroup>

          {results.labels.Labels.map((label, index) => (

            <ListGroup.Item 

              key={index}

              className={getConfidenceColorClass(label.Confidence)}

            >

              <div className="d-flex justify-content-between align-items-center">

                <span>{label.Name}</span>

                <Badge bg="primary" pill>

                  {label.Confidence.toFixed(2)}%

                </Badge>

              </div>

            </ListGroup.Item>

          ))}

        </ListGroup>

      </div>

    );

  };


  // Render faces results

  const renderFacesResults = () => {

    if (!results.faces) return null;

    

    return (

      <div className="result-container">

        <h4>Detected Faces</h4>

        <p>Found {results.faces.FaceDetails.length} faces in the image</p>

        

        <Row className="mt-3">

          {results.faces.FaceDetails.map((face, index) => (

            <Col key={index} md={6} className="mb-3">

              <Card className="h-100">

                <Card.Body>

                  <Card.Title>Face {index + 1}</Card.Title>

                  <ListGroup variant="flush">

                    <ListGroup.Item>

                      <strong>Age Range:</strong> {face.AgeRange.Low} - {face.AgeRange.High} years

                    </ListGroup.Item>

                    <ListGroup.Item>

                      <strong>Gender:</strong> {face.Gender.Value} ({face.Gender.Confidence.toFixed(2)}%)

                    </ListGroup.Item>

                    <ListGroup.Item>

                      <strong>Emotions:</strong>{' '}

                      {face.Emotions.map((emotion, i) => (

                        <Badge 

                          key={i} 

                          bg="secondary" 

                          className="me-1"

                        >

                          {emotion.Type} ({emotion.Confidence.toFixed(0)}%)

                        </Badge>

                      ))}

                    </ListGroup.Item>

                  </ListGroup>

                </Card.Body>

              </Card>

            </Col>

          ))}

        </Row>

      </div>

    );

  };


  // Render text results

  const renderTextResults = () => {

    if (!results.text) return null;

    

    return (

      <div className="result-container">

        <h4>Detected Text</h4>

        <p>Found {results.text.TextDetections.length} text elements in the image</p>

        

        {results.text.TextDetections.filter(text => text.Type === 'LINE').map((text, index) => (

          <Card key={index} className="mb-2">

            <Card.Body>

              <div className="d-flex justify-content-between align-items-center">

                <h5 className="mb-0">{text.DetectedText}</h5>

                <Badge bg="info" pill>

                  {text.Confidence.toFixed(2)}%

                </Badge>

              </div>

            </Card.Body>

          </Card>

        ))}

      </div>

    );

  };


  // Render moderation results

  const renderModerationResults = () => {

    if (!results.moderation) return null;

    

    return (

      <div className="result-container">

        <h4>Content Moderation</h4>

        

        {results.moderation.ModerationLabels.length === 0 ? (

          <Alert variant="success">

            No inappropriate content detected

          </Alert>

        ) : (

          <>

            <Alert variant="warning">

              Potentially inappropriate content detected

            </Alert>

            

            <ListGroup>

              {results.moderation.ModerationLabels.map((label, index) => (

                <ListGroup.Item 

                  key={index}

                  className={getConfidenceColorClass(label.Confidence)}

                >

                  <div className="d-flex justify-content-between align-items-center">

                    <div>

                      <span>{label.Name}</span>

                      {label.ParentName && (

                        <Badge 

                          bg="secondary" 

                          className="ms-2"

                        >

                          {label.ParentName}

                        </Badge>

                      )}

                    </div>

                    <Badge bg="danger" pill>

                      {label.Confidence.toFixed(2)}%

                    </Badge>

                  </div>

                </ListGroup.Item>

              ))}

            </ListGroup>

          </>

        )}

      </div>

    );

  };


  return (

    <Container>

      <h2 className="mb-4">Image Analysis</h2>

      

      <Form>

        <Form.Group className="mb-3">

          <Form.Label>Upload an image for analysis</Form.Label>

          <Form.Control

            type="file"

            accept="image/*"

            onChange={handleImageChange}

          />

          <Form.Text className="text-muted">

            Supported formats: JPG, PNG, and other common image formats

          </Form.Text>

        </Form.Group>

        

        {imagePreview && (

          <div className="text-center">

            <Image

              src={imagePreview}

              className="image-preview"

              alt="Preview"

            />

          </div>

        )}

        

        <Tabs

          activeKey={activeTab}

          onSelect={(tab) => setActiveTab(tab)}

          className="mt-4"

        >

          <Tab eventKey="labels" title="Labels">

            <div className="mt-3">

              <p>Detect objects, scenes, concepts, and celebrities in your image</p>

            </div>

          </Tab>

          <Tab eventKey="faces" title="Faces">

            <div className="mt-3">

              <p>Detect faces and analyze facial attributes like age, emotion, and gender</p>

            </div>

          </Tab>

          <Tab eventKey="text" title="Text">

            <div className="mt-3">

              <p>Detect and extract text found in your image</p>

            </div>

          </Tab>

          <Tab eventKey="moderation" title="Moderation">

            <div className="mt-3">

              <p>Detect potentially unsafe or inappropriate content</p>

            </div>

          </Tab>

        </Tabs>

        

        <div className="mt-3">

          <Button

            variant="primary"

            onClick={analyzeSelectedImage}

            disabled={!image || loading}

          >

            {loading ? (

              <>

                <Spinner

                  as="span"

                  animation="border"

                  size="sm"

                  role="status"

                  aria-hidden="true"

                  className="me-2"

                />

                Analyzing...

              </>

            ) : (

              `Analyze Image (${activeTab})`

            )}

          </Button>

        </div>

      </Form>

      

      {error && (

        <Alert variant="danger" className="mt-4">

          {error}

        </Alert>

      )}

      

      <div className="mt-4">

        {activeTab === 'labels' && renderLabelsResults()}

        {activeTab === 'faces' && renderFacesResults()}

        {activeTab === 'text' && renderTextResults()}

        {activeTab === 'moderation' && renderModerationResults()}

      </div>

    </Container>

  );

};


export default ImageAnalysis;
